import React from 'react';
import { ShoppingCart, X, Minus, Plus, Trash2, Send } from 'lucide-react';
import { CartItem } from './publicMenuTypes';

type Props = {
  open: boolean;
  tableNumber: string | undefined;
  cartItems: CartItem[];
  customerName: string;
  orderLoading: boolean;
  cartTotal: number;
  onClose: () => void;
  onUpdateQuantity: (cartItemId: string, quantity: number) => void;
  onRemove: (cartItemId: string) => void;
  onClear: () => void;
  onPlaceOrder: () => void;
};

const PublicMenuCartModal: React.FC<Props> = ({
  open,
  tableNumber,
  cartItems,
  customerName,
  orderLoading,
  cartTotal,
  onClose,
  onUpdateQuantity,
  onRemove,
  onClear,
  onPlaceOrder,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-zinc-200 p-4">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-zinc-900">Cart · Table {tableNumber}</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-zinc-400 hover:bg-zinc-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {cartItems.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">Your cart is empty.</div>
          ) : (
            <ul className="divide-y divide-zinc-100 p-3">
              {cartItems.map((cartItem) => (
                <li key={cartItem.id} className="flex flex-col gap-2 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-zinc-900">{cartItem.menuItemName}</p>
                      <p className="text-xs text-amber-700">₹{cartItem.unitPrice.toFixed(2)} each</p>
                    </div>
                    <button type="button" onClick={() => onRemove(cartItem.id)} className="shrink-0 text-red-500 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onUpdateQuantity(cartItem.id, cartItem.quantity - 1)}
                        className="rounded-full bg-red-50 p-1.5 text-red-600 hover:bg-red-100"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="min-w-[2rem] text-center font-semibold">{cartItem.quantity}</span>
                      <button
                        type="button"
                        onClick={() => onUpdateQuantity(cartItem.id, cartItem.quantity + 1)}
                        className="rounded-full bg-emerald-50 p-1.5 text-emerald-600 hover:bg-emerald-100"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    <span className="font-bold text-zinc-900">₹{cartItem.totalPrice.toFixed(2)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {cartItems.length > 0 && (
          <div className="border-t border-zinc-200 bg-zinc-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="font-semibold text-zinc-900">Total</span>
              <span className="text-xl font-bold text-amber-600">₹{cartTotal.toFixed(2)}</span>
            </div>
            <button
              type="button"
              onClick={onPlaceOrder}
              disabled={orderLoading || !customerName}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 font-bold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {orderLoading ? (
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <>
                  <Send className="h-5 w-5" />
                  Place order
                </>
              )}
            </button>
            <button type="button" onClick={onClear} className="mt-2 w-full rounded-lg bg-zinc-200 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-300">
              Clear cart
            </button>
            <p className="mt-2 text-center text-[10px] text-zinc-500">Sent to the kitchen. Payment may be collected separately.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicMenuCartModal;
